import { Autocomplete, CircularProgress, IconButton, InputAdornment, TextField, useTheme } from '@mui/material';
import { FC, SyntheticEvent, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectBuildsForDeploy, selectGetBuildsForDeployStatus } from '@pages/stands/store/selectors';
import { StandBuild } from '@pages/stands/types';
import ClearIcon from '@mui/icons-material/Clear';
import { useTranslation } from 'react-i18next';
import ModalErrorContainer from '@shared/components/modals/ModalErrorContainer';
import { getBuildDetailsReset, getBuildsForDeployError } from '@pages/stands/store';
import { RequestStatusEnum } from '@shared/types';

interface BuildSelectorProps {
    value: StandBuild;
    onChange: (event: SyntheticEvent<Element, Event>, value: StandBuild | null) => void;
    handleClear?: () => void;
    disabled?: boolean;
    error?: boolean;
}

const BuildSelector: FC<BuildSelectorProps> = ({ value, onChange, handleClear, disabled, error }) => {
    const [open, setOpen] = useState(false);
    const getBuildsForDeployStatus = useSelector(selectGetBuildsForDeployStatus);
    const buildsForDeploy = useSelector(selectBuildsForDeploy);
    const isLoadingBuilds = getBuildsForDeployStatus === RequestStatusEnum.Loading;
    const theme = useTheme();

    const { t } = useTranslation();

    const handleOpenBuildSelector = () => {
        setOpen(true);
    };

    const handleCloseBuildSelector = () => {
        setOpen(false);
    };

    return (
        <>
            <Autocomplete
                fullWidth
                open={open}
                onOpen={handleOpenBuildSelector}
                onClose={handleCloseBuildSelector}
                value={value}
                onChange={onChange}
                options={['last', ...buildsForDeploy]}
                loading={isLoadingBuilds}
                disabled={disabled}
                disableClearable
                size="small"
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={t('build')}
                        size="small"
                        error={error}
                        helperText={error ? t('specify_build') : ''}
                        slotProps={{
                            input: {
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {params.InputProps.endAdornment}
                                        {isLoadingBuilds ? (
                                            <InputAdornment position="start">
                                                <CircularProgress size={18} />
                                            </InputAdornment>
                                        ) : (
                                            Boolean(value?.length) &&
                                            handleClear && (
                                                <InputAdornment position="start" sx={{ mr: -1 }}>
                                                    <IconButton size="small" onClick={handleClear}>
                                                        <ClearIcon />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        )}
                                    </>
                                ),

                                sx: {
                                    '&.Mui-disabled': {
                                        backgroundColor: theme.palette.action.hover,
                                    },
                                    '&.MuiInputBase-root': {
                                        pr: 0,
                                    },
                                    height: 37,
                                },
                            },
                        }}
                    />
                )}
            />
            {getBuildsForDeployStatus === RequestStatusEnum.Error && (
                <ModalErrorContainer
                    object={t('build').toLowerCase()}
                    action={t('fetching')}
                    resetActionCallback={getBuildDetailsReset}
                    errorSelector={getBuildsForDeployError}
                />
            )}
        </>
    );
};

export default BuildSelector;
